import { Controller } from "@hotwired/stimulus"

// Screen 2: which destination, how far past its boundary, and who is asking.
//
// The choice is reported to PostHog as soon as it is made, not only when a sale
// closes -- a destination that looked and left is exactly who sales wants to
// follow up with.
export default class extends Controller {
  static targets = [
    "query", "results", "chip", "label",
    "coverage", "organization", "summary", "summaryLabel", "count", "nearby",
    "fields", "submit",
  ]
  static values = { locationSearchUrl: String, summaryUrl: String, analytics: Object }

  connect() {
    this.destination = null
    this.render()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
  }

  toggle(element, visible) {
    element.style.display = visible ? "" : "none"
  }

  search() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.runSearch(), 250)
  }

  async runSearch() {
    const q = this.queryTarget.value.trim()
    if (q.length < 3) { this.resultsTarget.innerHTML = ""; return }

    const response = await fetch(`${this.locationSearchUrlValue}?q=${encodeURIComponent(q)}`, {
      headers: { Accept: "application/json" }
    })
    if (!response.ok) return

    const { matches, places } = await response.json()
    if (matches.length === 0 && places.length === 0) {
      this.resultsTarget.innerHTML = `<li class="px-4 py-3 aon-body-smaller text-gray-600">Nothing on the Atlas matches “${esc(q)}”.</li>`
      return
    }

    // Destinations first, and it costs the business case nothing: the location
    // search is a prefix match, so a business name matches no destination at
    // all and the group is dropped. Typing a city, meanwhile, would otherwise
    // bury the one answer wanted under a screen of fuzzy title matches.
    this.resultsTarget.innerHTML = [
      this.group("Destinations", matches.map((m) => this.destinationRow(m))),
      this.group("Individual Places", places.map((p) => this.placeRow(p))),
    ].join("")
  }

  group(heading, rows) {
    if (rows.length === 0) return ""

    return `<li class="aon-label-small border-b border-gray-200 bg-gray-50 px-4 py-2 text-gray-600">${heading}</li>` + rows.join("")
  }

  // Picking a business sets the destination to its own city and carries the
  // place through, so the buyer lands on a list that certainly contains it.
  placeRow(place) {
    return `
      <li>
        <button type="button" data-action="amplify-destination#pick"
                data-label="${esc(place.location)}" data-city="${esc(place.city)}"
                data-state="${esc(place.state)}" data-country="${esc(place.country)}"
                data-place-id="${place.place_id}"
                class="flex w-full items-baseline justify-between gap-4 px-4 py-3 text-left hover:bg-aon-tan">
          <span class="aon-body-small text-aon-black">${esc(place.title)}</span>
          <span class="aon-body-smaller shrink-0 text-gray-500">${esc(place.location)}</span>
        </button>
      </li>`
  }

  destinationRow(match) {
    return `
      <li>
        <button type="button" data-action="amplify-destination#pick"
                data-label="${esc(match.label)}" data-city="${esc(match.city)}"
                data-state="${esc(match.state)}" data-country="${esc(match.country)}"
                class="flex w-full items-baseline justify-between gap-4 px-4 py-3 text-left hover:bg-aon-tan">
          <span class="aon-body-small text-aon-black">${esc(match.label)}</span>
          <span class="aon-body-smaller shrink-0 text-gray-500">${match.place_count} ${match.place_count === 1 ? "place" : "places"}</span>
        </button>
      </li>`
  }

  pick(event) {
    const { label, city, state, country, placeId } = event.currentTarget.dataset
    this.destination = { label, city, state, country }
    this.preselectPlaceId = placeId || null
    this.queryTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.render()
    this.loadSummary()
  }

  clear() {
    this.destination = null
    this.preselectPlaceId = null
    this.render()
  }

  // Coverage or organization changed. Only the summary depends on coverage.
  refresh() {
    if (this.destination) this.loadSummary()
  }

  selectOrganization() {
    if (this.organizationTarget.value) this.trackSelection("org_type_selected")
  }

  async loadSummary() {
    const params = new URLSearchParams({ coverage_miles: this.coverageTarget.value })
    for (const key of ["city", "state", "country"]) {
      if (this.destination[key]) params.set(key, this.destination[key])
    }

    const response = await fetch(`${this.summaryUrlValue}?${params}`, {
      headers: { Accept: "application/json" }
    })
    if (!response.ok) return

    const summary = await response.json()
    this.renderSummary(summary)

    this.trackSelection("destination_searched", summary)
    // A destination we have nothing to sell in is worth knowing about on its
    // own: it is a coverage gap, not a failed search.
    if (summary.total_count === 0) this.trackSelection("zero_coverage_hit", summary)
  }

  renderSummary(summary) {
    const total = summary.total_count
    this.countTarget.textContent = `${total} ${total === 1 ? "place" : "places"}`
    this.summaryLabelTarget.textContent = this.destination.label
    this.nearbyTarget.textContent = summary.nearby_count > 0
      ? `${summary.inside_count} inside ${this.destination.label}, and ${summary.nearby_count} within ${summary.coverage_miles} miles that travelers are finding anyway.`
      : ""
    this.toggle(this.summaryTarget, true)
    this.submitTarget.disabled = total === 0
  }

  render() {
    const picked = this.destination !== null
    this.toggle(this.chipTarget, picked)
    this.toggle(this.queryTarget, !picked)
    if (picked) this.labelTarget.textContent = this.destination.label

    if (!picked) {
      this.toggle(this.summaryTarget, false)
      this.submitTarget.disabled = true
    }

    this.fieldsTarget.innerHTML = picked
      ? ["label", "city", "state", "country"]
        .map((key) => `<input type="hidden" name="${key}" value="${esc(this.destination[key] || "")}">`)
        .join("") +
        (this.preselectPlaceId ? `<input type="hidden" name="preselect_place_id" value="${esc(this.preselectPlaceId)}">` : "")
      : ""
  }

  confirm() {
    this.trackSelection("expand_coverage_submitted")
  }

  // sendBeacon on confirm because the click navigates away immediately.
  trackSelection(event, summary = null) {
    if (typeof window.posthog === "undefined" || !window.posthog.capture) return

    const properties = {
      ...this.analyticsValue,
      destination: this.destination?.label,
      destination_city: this.destination?.city,
      destination_state: this.destination?.state,
      destination_country: this.destination?.country,
      coverage_miles: Number(this.coverageTarget.value),
      organization_type: this.organizationTarget.value || null,
    }
    if (summary) {
      properties.places_inside = summary.inside_count
      properties.places_nearby = summary.nearby_count
      properties.places_total = summary.total_count
    }

    const options = event === "expand_coverage_submitted" ? { transport: "sendBeacon" } : {}
    window.posthog.capture(event, properties, options)
  }
}

function esc(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ))
}
